import type { Campaign, Variant } from '~/modules/campaign/types';
import type { TemplateInfo } from '~/types/template';

import {
  getSearchParamByCampaign,
  useCampaignVariantPreview,
  findVariantByOrigin,
} from '~/modules/campaign';
import { isVariantSamePageType } from '~/modules/campaign-template';
import { parseTemplateInfoBy } from '~/utils';

export const useCampaignPreview = () => {
  const { getLinkPreview } = useCampaignVariantPreview();

  const createUrlObject = (urlPreview: string, searchParams: string) => {
    const url = new URL(urlPreview);
    url.search = searchParams;
    return {
      href: url.href,
      pathname: url.pathname,
      search: url.search,
    };
  };

  const handlePreviewControl = async (template: TemplateInfo, campaign: Campaign) => {
    const urlPreview = await getLinkPreview(template);
    if (!urlPreview) return;

    const searchParams = getSearchParamByCampaign(campaign, { active: 'variantA' });
    return createUrlObject(urlPreview, searchParams);
  };

  const parseUrlPreviewBy = async (variant: Variant, campaign?: Campaign) => {
    const { templateSuffix } = variant;
    const templateInfo = parseTemplateInfoBy({ templateSuffix });

    if (!templateInfo || !campaign) return;

    const control = findVariantByOrigin(campaign, true);
    if (!control) return;

    const isViewControl = control.templateSuffix === templateSuffix;
    if (isViewControl) return handlePreviewControl(templateInfo, campaign);

    const isViewVariantSamePageType = isVariantSamePageType([control, variant]);
    if (isViewVariantSamePageType) return handlePreviewVariantSameType(campaign);

    const nonOriginVariant = findVariantByOrigin(campaign, false);
    return handlePreviewVarianByRedirectUrl(nonOriginVariant, campaign);
  };

  /**
   * Handle preview variant same page type
   * @description When view variant same page type, using url of `control` but active to `variantB`
   * @param campaign
   * @returns
   */
  const handlePreviewVariantSameType = async (campaign: Campaign) => {
    const control = findVariantByOrigin(campaign, true);
    if (!control) return;

    const { templateSuffix } = control;
    const templateInfo = parseTemplateInfoBy({ templateSuffix });
    if (!templateInfo) return;

    const urlPreview = await getLinkPreview(templateInfo);
    if (!urlPreview) return;

    const searchParams = getSearchParamByCampaign(campaign, { active: 'variantB' });
    return createUrlObject(urlPreview, searchParams);
  };

  const handlePreviewVarianByRedirectUrl = (variant: Variant | undefined, campaign: Campaign) => {
    if (!variant?.template?.redirectURL) return;

    const searchParams = getSearchParamByCampaign(campaign, { active: 'variantB' });
    return createUrlObject(variant.template.redirectURL, searchParams);
  };

  return {
    parseUrlPreviewBy,
  };
};
